import { type Graph } from '../models/graph';

export function kruskal(graph: Graph): string[] {
  if (graph.nodes.length === 0) return [];

  const sortedEdges = [...graph.edges].sort((a, b) => a.weight - b.weight);
  const parent = new Map<string, string>();
  const rank = new Map<string, number>();

  for (const node of graph.nodes) {
    parent.set(node.id, node.id);
    rank.set(node.id, 0);
  }

  function find(x: string): string {
    if (parent.get(x) !== x) parent.set(x, find(parent.get(x)!));
    return parent.get(x)!;
  }

  function union(x: string, y: string): boolean {
    const rootX = find(x);
    const rootY = find(y);
    if (rootX === rootY) return false;

    const rankX = rank.get(rootX)!;
    const rankY = rank.get(rootY)!;
    if (rankX < rankY) {
      parent.set(rootX, rootY);
    } else if (rankX > rankY) {
      parent.set(rootY, rootX);
    } else {
      parent.set(rootY, rootX);
      rank.set(rootX, rankX + 1);
    }
    return true;
  }

  const mstEdges: string[] = [];
  for (const edge of sortedEdges) {
    if (union(edge.sourceId, edge.targetId)) {
      mstEdges.push(edge.id);
      if (mstEdges.length === graph.nodes.length - 1) break;
    }
  }

  return mstEdges;
}

export function mstWeight(graph: Graph): number {
  return kruskal(graph).reduce(
    (sum, id) => sum + (graph.edges.find((e) => e.id === id)?.weight ?? 0),
    0,
  );
}
